import type { PixelEvent } from '@/events';
import type { PixelLogger } from '@/logger';
import { fetchWithTimeout } from '../network/fetchWithTimeout';

import type { CompressionStrategy } from '../compression/types';
import type { EventPayloadBuilder } from './EventPayloadBuilder';
import type { ResolvedHttpSenderConfig } from './endpoint-resolver';
import type { EventSender, SendOptions, SenderDebugState } from './types';

export type HttpSenderResponseLog = {
  readonly at: number;
  readonly url: string;
  readonly status: number;
  readonly ack: boolean;
  readonly eventCount: number;
  readonly body?: unknown;
  readonly error?: string;
};

export class HttpSender implements EventSender {
  private readonly config: ResolvedHttpSenderConfig;

  private readonly payloadBuilder: EventPayloadBuilder;

  private readonly compression: CompressionStrategy;

  private readonly logger: PixelLogger;

  private sentCount = 0;

  private lastAck: boolean | null = null;

  private readonly responseLogs: HttpSenderResponseLog[] = [];

  private lastCompression: {
    originalSize: number;
    compressedSize: number;
  } | null = null;

  constructor(
    config: ResolvedHttpSenderConfig,
    payloadBuilder: EventPayloadBuilder,
    compression: CompressionStrategy,
    logger: PixelLogger
  ) {
    this.config = config;
    this.payloadBuilder = payloadBuilder;
    this.compression = compression;
    this.logger = logger;
  }

  async send(
    events: PixelEvent[],
    options?: SendOptions
  ): Promise<{ ack: boolean; eventCount: number }> {
    if (events.length === 0) {
      return { ack: true, eventCount: 0 };
    }

    if (options?.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    const deliveryMode =
      options?.deliveryMode ?? (events.length > 1 ? 'batch' : 'single');

    if (deliveryMode === 'single' && events.length > 1) {
      return this.sendIndividually(events, options);
    }

    if (deliveryMode === 'batch' && !this.config.batchEndpoint) {
      if (this.config.batchFallback === 'single') {
        return this.sendIndividually(events, options);
      }
      throw new Error('[HttpSender] Batch endpoint is not configured.');
    }

    const useBatch = deliveryMode === 'batch' && events.length > 1;
    const url = useBatch
      ? this.config.batchEndpoint!
      : this.config.eventEndpoint;
    const rawBody = useBatch
      ? JSON.stringify(this.payloadBuilder.buildBatchPayload(events))
      : JSON.stringify(this.payloadBuilder.buildApiEvent(events[0]!));

    const compressed = this.compression.compress(rawBody);
    this.lastCompression = {
      originalSize: compressed.originalSize,
      compressedSize: compressed.compressedSize,
    };

    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          ...this.config.headers,
          'Content-Type': 'application/json',
          'Content-Encoding': compressed.encoding,
        },
        body: compressed.body,
        keepalive: true,
        timeoutMs: this.config.timeoutMs,
        signal: options?.signal,
      });

      const body = await response.json().catch(() => ({}));

      const ack = response.ok;
      this.lastAck = ack;

      if (ack) {
        this.sentCount += events.length;
      }

      this.responseLogs.push({
        at: Date.now(),
        url: redactEndpoint(url),
        status: response.status,
        ack,
        eventCount: events.length,
        body,
      });

      if (!ack) {
        throw new Error(`[HttpSender] HTTP ${response.status}`);
      }

      this.logger.debug(`[HttpSender] Sent ${events.length} event(s) — ACK`);

      return { ack: true, eventCount: events.length };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.lastAck = false;

      this.responseLogs.push({
        at: Date.now(),
        url: redactEndpoint(url),
        status: 0,
        ack: false,
        eventCount: events.length,
        error: message,
      });

      throw error;
    }
  }

  getState(): SenderDebugState & {
    responses: readonly HttpSenderResponseLog[];
    compression: { originalSize: number; compressedSize: number } | null;
    mode: 'internal' | 'external';
  } {
    return {
      sentCount: this.sentCount,
      logs: this.responseLogs.map(
        log =>
          `[HttpSender] ${log.status} ack=${log.ack} events=${log.eventCount}${log.error ? ` err=${log.error}` : ''}`
      ),
      lastAck: this.lastAck,
      responses: [...this.responseLogs],
      compression: this.lastCompression,
      mode: this.config.mode,
    };
  }

  clear(): void {
    this.sentCount = 0;
    this.lastAck = null;
    this.responseLogs.length = 0;
    this.lastCompression = null;
  }

  private async sendIndividually(
    events: PixelEvent[],
    options?: SendOptions
  ): Promise<{ ack: boolean; eventCount: number }> {
    let eventCount = 0;
    for (const event of events) {
      const result = await this.send([event], {
        ...options,
        deliveryMode: 'single',
      });
      if (!result.ack) {
        throw new Error('[HttpSender] Individual send not acknowledged.');
      }
      eventCount += result.eventCount;
    }
    return { ack: true, eventCount };
  }
}

function redactEndpoint(endpoint: string): string {
  try {
    return new URL(endpoint).origin;
  } catch {
    return '[Present]';
  }
}
